import React, { useState } from "react"
import { Col, Row } from "reactstrap"
import { useHistory } from "react-router-dom"
import moment from "moment"

import { SearchBox } from "components/SearchBox"
import { DataTable } from "components/DataTable"
import { Pagination } from "components/Pagination"
import { DateRangePicker } from "components/DatePicker"
import { AutoComplete } from "components/AutoComplete"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"
import { GetOrderedTrailersListing } from "api/api.service"
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils"
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest"
import { APP_ROUTES } from "helpers/routeHelpers"

import ActionEyeIcon from "assets/images/icons/feather-eye-icon.png"
import ConfiguratorOrderStatus from "constants/ConfiguratorOrderStatus"
import { TrailerSize } from "constants"

const COLUMNS = {
   TRAILER_TYPE: "Trailer Type",
   ORDER_PLACED: "Order Placed",
   STATUS: "Status",
   TRAILER_NAME: "Trailer Name",
   TRAILER_SIZE_MODEL: "Size/Model",
   CUSTOMER_NAME: "Customer Name",
   CUSTOMER_PHONE: "Phone",
   CUSTOMER_EMAIL: "Email",
   ORDER_DATE: "Order Date",
   ACTIONS: "Actions",
}

const statusSelectOptions = Object.values(ConfiguratorOrderStatus).map(
   status => ({
      label: ConfiguratorOrderStatus.getDisplayTextKey(status),
      value: status,
   })
)

const DATA_KEY = "data"
const TOTAL_KEY = "total"

const initialRangeValue = [
   {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
   },
]

const ConfiguratorOrders = () => {
   const history = useHistory()

   const [statusFilters, setStatusFilters] = useState(null)
   const [range, setRange] = useState(initialRangeValue)
   const [hasUserSelectedDate, setHasUserSelectedDate] = useState(false)

   const {
      data,
      isFetching,
      page,
      searchText,
      pageSize,
      total,
      handleSearchChange,
      handlePageClick,
      onChangePageSize,
   } = useLocalPaginatedRequest({
      requestFn: GetOrderedTrailersListing,
      params: {
         ...(statusFilters?.value && { status: statusFilters?.value }),
         ...getDateRangeParams(range[0]),
      },
      deps: [statusFilters, range],
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
   })

   function getDateRangeParams(dateRange) {
      if (!hasUserSelectedDate) return {}
      const params = {}
      if (dateRange.startDate) {
         params.fromDate = moment(dateRange.startDate)
            .startOf("day")
            .format(dateTimeFormat.apiDateTimeFormat)
      }
      if (dateRange.endDate) {
         params.toDate = moment(dateRange.endDate)
            .endOf("day")
            .format(dateTimeFormat.apiDateTimeFormat)
      }
      return params
   }

   const handleViewTrailerDetails = data => {
      const combinedId = `${data.id}-${data.referenceNo}`
      history.push(
         `${APP_ROUTES.TIMELINE_CONFIGURATOR_ORDER_DETAILS.replace(
            ":id",
            combinedId
         )}`
      )
   }

   const handleSelectStatusOption = option => {
      setStatusFilters(option)
   }
   const handleDateChange = ({ selection }) => {
      const { startDate, endDate } = selection
      let safeEndDate = endDate
      if (!(endDate instanceof Date) || isNaN(endDate)) {
         safeEndDate = startDate
      }

      const adjustedSelection = {
         ...selection,
         endDate: safeEndDate,
      }

      setRange([adjustedSelection])
      setHasUserSelectedDate(true)
   }
   const handleClearDateRange = setIsOpen => {
      setIsOpen(false)
      setRange(initialRangeValue)
      setHasUserSelectedDate(false)
   }

   const formConfig = [
      {
         title: COLUMNS.TRAILER_TYPE,
         className: "",
         render: data =>
            `${TrailerSize.getDisplayTextKey(
               data.configuratorTrailer.configuratorTrailerSizeType[0]
            )} ${data.configuratorTrailer.trailerType.trailerType} ${
               data.configuratorTrailer.model
            }`,
      },
      {
         title: COLUMNS.CUSTOMER_NAME,
         className: "",
         render: data =>
            `${data?.customer?.firstName} ${data?.customer?.lastName}` || "-",
      },

      {
         title: COLUMNS.CUSTOMER_EMAIL,
         className: "",
         render: data => data?.customer.email || "-",
      },

      {
         title: COLUMNS.CUSTOMER_PHONE,
         className: "",
         render: data => data?.customer?.phoneNo || "-",
      },
      {
         title: COLUMNS.ORDER_PLACED,
         className: "",
         render: data => {
            return convertDateTime({
               date: data.createdAt,
               customFormat: dateTimeFormat.casitaCustomDateFormat,
               dateOnly: true,
            })
         },
      },
      {
         title: COLUMNS.STATUS,
         className: "",
         render: data => (
            <div
               className={`configurator-order-status ${ConfiguratorOrderStatus.getLabelClass(
                  data.status
               )}`}
            >
               <span>
                  {ConfiguratorOrderStatus.getDisplayTextKey(data.status) ||
                     "-"}
               </span>
            </div>
         ),
      },

      {
         title: COLUMNS.ACTIONS,

         render: data => (
            <div className="d-flex gap-2 pointer">
               <img
                  src={ActionEyeIcon}
                  onClick={() => handleViewTrailerDetails(data)}
               />
            </div>
         ),
      },
   ]

   return (
      <>
         <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between admin-inventory-management-general-inventory-responsiveness ">
               <div className="  me-2 ">
                  <div className="d-flex justify-content-end align-items-end admin-inventory-request-autocomplete gap-2 flex-wrap">
                     <div className="d-flex">
                        <AutoComplete
                           value={statusFilters}
                           isClearable
                           onChange={val => handleSelectStatusOption(val)}
                           isSearchable={false}
                           placeholder="Select Status"
                           customStyles={autoCompleteStyles.STYLE_SIX}
                           classNamePrefix="status-header-search-admin"
                           options={statusSelectOptions}
                           className="min-250-w"
                           menuPortalTarget={document.body}
                        />
                     </div>
                     <div className="min-250-w d-flex">
                        <DateRangePicker
                           range={range}
                           onChange={handleDateChange}
                           onClear={handleClearDateRange}
                           showClearIcon={true}
                           hasUserSelectedDate={hasUserSelectedDate}
                        />
                     </div>
                  </div>
               </div>
               <div className="ad-location-search-box">
                  <SearchBox
                     inputClasses="vendor-search-input input-search"
                     labelClass="d-none"
                     placeholder="Search"
                     searchIcon={true}
                     searchIconClass="vendor-table-search-icon"
                     conClass="vendor-table-search-inner-class"
                     searchText={searchText}
                     onChange={handleSearchChange}
                  />
               </div>
            </div>
         </div>
         <Row>
            <Col md={12}>
               <DataTable
                  data={data}
                  tableClasses="vendor-management-table"
                  loading={isFetching}
                  className="vendor-data-container mt-3"
                  config={formConfig}
               />
            </Col>
            <Pagination
               currentPage={page}
               totalData={total}
               onPageClick={handlePageClick}
               rowLimit={pageSize}
               onChangePageSize={onChangePageSize}
            />
         </Row>
      </>
   )
}

export default ConfiguratorOrders
